import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get, useEventListener, useToggle } from '@codex/helpers';
import MenuItem from './MenuItem';
import PriceDropNotification from './PriceDropNotification';
import StatusChangeNotification from './StatusChangeNotification';

const NOTIFICATION_TYPE_PRICE_DROP = 'PRICE_DROP';
const NOTIFICATION_TYPE_STATUS_CHANGE = 'ORDER_STATUS_CHANGE';
const MAX_NOTIFICATIONS_PER_TYPE = 2;

const whatInput =
  typeof window !== 'undefined'
    ? require('what-input')
    : { ask: () => undefined };

const MenuContainer = styled.div`
  position: static;

  @media ${({ theme }) => theme.breakpoints.mobileMin} {
    position: relative;
  }
`;

const MenuPosition = styled.div`
  position: absolute;
  top: 47px;
  left: 0px;
  right: 1px;
  width: auto;
  z-index: 1000;

  @media ${({ theme }) => theme.breakpoints.mobileMin} {
    top: 26px;
    right: -38px;
    width: 320px;
    left: auto;
  }

  @media ${({ theme }) => theme.breakpoints.desktop} {
    top: 45px;
    right: -70px;
  }
`;

const Wrapper = styled.ul`
  min-width: 150px;
  padding: 0;
  margin: 0;
  background-color: ${props => props.theme.colors.shades[7]};
  border: solid 1px ${props => props.theme.colors.shades[5]};

  @media ${({ theme }) => theme.breakpoints.mobile} {
    width: 100%;
  }
`;

const isMouseDevice = () => whatInput.ask('loose') !== 'touch' && whatInput.ask('loose') !== null;

const AccountMenu = (props) => {
  const [priceDropNotifications, setPriceDropNotifications] = useState([]);
  const [
    priceDropNotificationsTotalCount,
    setPriceDropNotificationsTotalCount
  ] = useState(0);
  const [statusChangeNotifications, setStatusChangeNotifications] = useState(
    []
  );
  const [
    statusChangeNotificationsTotalCount,
    setStatusChangeNotificationsTotalCount
  ] = useState(0);
  const [open, toggle, menuRef] = useToggle(false, {
    clickOutside: true,
    escape: true
  });

  const openMenuByMouse = useCallback(() => {
    if (isMouseDevice) {
      toggle(true);
    }
  }, []);

  const closeMenuByMouse = useCallback(() => {
    if (isMouseDevice) {
      toggle(false);
    }
  }, []);

  const toggleMenu = useCallback(
    (event) => {
      if (whatInput.ask() !== 'mouse') {
        event.preventDefault();
      }

      toggle(!open);
    },
    [open, whatInput]
  );

  const [notificationsChanged, setNotificationsChanged] = useState(0);
  useEventListener('notificationsChanged', () => {
    setNotificationsChanged(n => n + 1);
  });

  useEffect(() => {
    if (props.notificationsApi) {
      get(props.notificationsApi).then((notifications) => {
        const allPriceDrop = notifications.filter(
          n => n.type === NOTIFICATION_TYPE_PRICE_DROP
        );
        const allStatusChange = notifications.filter(
          n => n.type === NOTIFICATION_TYPE_STATUS_CHANGE
        );

        setPriceDropNotificationsTotalCount(allPriceDrop.length);
        setPriceDropNotifications(
          allPriceDrop.slice(0, MAX_NOTIFICATIONS_PER_TYPE)
        );
        setStatusChangeNotificationsTotalCount(allStatusChange.length);
        setStatusChangeNotifications(
          allStatusChange.slice(0, MAX_NOTIFICATIONS_PER_TYPE)
        );
      });
    }
  }, [props.notificationsApi, notificationsChanged]);

  useEffect(() => {
    if (open) {
      const analyticsData = priceDropNotifications.map((notification) => {
        const currentPrice = notification.payload.currentPrice;
        const savePrice = notification.payload.savePrice;
        const previousPrice = Number(currentPrice) + Number(savePrice);
        const productId = notification.payload.productId;
        return {
          currentValue: currentPrice.toString(),
          previousValue: previousPrice.toString(),
          fupid: productId.toString()
        };
      });
      props.onNotificationsShown(analyticsData);
    }
  }, [priceDropNotifications, open]);

  const children = props.children({
    notificationsCount:
      priceDropNotificationsTotalCount + statusChangeNotificationsTotalCount
  });

  return (
    <MenuContainer
      ref={menuRef}
      onMouseEnter={openMenuByMouse}
      onMouseLeave={closeMenuByMouse}
    >
      {React.Children.count(children) === 1
        ? React.cloneElement(children, {
          onClick: toggleMenu,
          openSubMenu: open
        })
        : children}

      {(!children || open) && (
        <MenuPosition
          offsetTop={props.offsetTop}
          offsetRight={props.offsetRight}
        >
          <Wrapper>
            <MenuItem
              icon="BoxOpenFull"
              label={props.items.orders.label}
              link={props.items.orders.link}
              notificationsLabel={props.items.orders.notificationsLabel}
              notificationsCount={statusChangeNotificationsTotalCount}
              dataInteraction="Main Header: Account: Orders"
              qa="your-orders-option"
              onClick={toggle}
            >
              {statusChangeNotifications.map(statusChange => (
                <StatusChangeNotification
                  key={statusChange.id}
                  {...statusChange.payload}
                />
              ))}
            </MenuItem>

            <MenuItem
              icon="HeartOutline"
              label={props.items.savedForLater.label}
              link={props.items.savedForLater.link}
              notificationsLabel={props.items.savedForLater.notificationsLabel}
              notificationsCount={priceDropNotificationsTotalCount}
              dataInteraction="Main Header: Account: Save for Later List"
              qa="wishlist-option"
              onClick={toggle}
            >
              {priceDropNotifications.map(priceDrop => (
                <PriceDropNotification
                  key={priceDrop.id}
                  savedForLaterLink={props.items.savedForLater.link}
                  {...priceDrop.payload}
                />
              ))}
            </MenuItem>

            <MenuItem
              icon="MyDetails"
              label={props.items.details.label}
              link={props.items.details.link}
              dataInteraction="Main Header: Account: Manage Details"
              qa="manage-your-details-option"
              onClick={toggle}
            />

            <MenuItem
              icon="Cog"
              label={props.items.stayInTouch.label}
              link={props.items.stayInTouch.link}
              dataInteraction="Main Header: Account: Manage Communications"
              qa="keep-up-to-date-option"
              onClick={toggle}
            />

            <MenuItem
              icon="Help"
              label={props.items.findOutMore.label}
              link={props.items.findOutMore.link}
              dataInteraction="Main Header: Account: Find Information"
              qa="questions-option"
              onClick={toggle}
            />

            <MenuItem
              icon="SignOut"
              label={props.items.signOut.label}
              link={props.items.signOut.link}
              dataInteraction="Main Header: Account: Sign Out"
              qa="sign-out-option"
              onClick={toggle}
            />
          </Wrapper>
        </MenuPosition>
      )}
    </MenuContainer>
  );
};

AccountMenu.propTypes = {
  children: PropTypes.func.isRequired,
  containerRef: PropTypes.shape({}),
  items: PropTypes.shape({}).isRequired,
  notificationsApi: PropTypes.string,
  offsetTop: PropTypes.number,
  offsetRight: PropTypes.number,
  onNotificationsShown: PropTypes.func
};

AccountMenu.defaultProps = {
  containerRef: null,
  offsetTop: 0,
  offsetRight: 0,
  notificationsApi: null,
  onNotificationsShown: () => {}
};

export default AccountMenu;
